import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { CustomTooltip } from '../ui';

// Hook: detect mobile layout
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Map value + thresholds to color (green/yellow/red)
const valueColor = (value, thresholds) => {
  if (value === undefined || value === null) return '#94a3b8'; // slate-400
  const v = Number(value);
  if (!Number.isFinite(v)) return '#94a3b8';
  if (v < thresholds.min) return '#f59e0b'; // yellow (bajo crítico)
  if (v < thresholds.idealMin) return '#f59e0b'; // yellow (bajo)
  if (v > thresholds.max) return '#ef4444'; // red (alto crítico)
  if (v > thresholds.idealMax) return '#ef4444'; // red (alto)
  return '#22c55e'; // green (ideal)
};

/**
 * Charts section component
 */
export const ChartsSection = ({ history, latestData, sensorConfig }) => {
  const isMobile = useIsMobile();
  const chartHeight = isMobile ? 220 : 300;
  const fontSize = isMobile ? 9 : 11;

  const tempColor1 = valueColor(latestData?.temp1, sensorConfig.temperature);
  const tempColor2 = valueColor(latestData?.temp2, sensorConfig.temperature);
  const doColor1 = valueColor(latestData?.do1_mgL, sensorConfig.oxygen);
  const doColor2 = valueColor(latestData?.do2_mgL, sensorConfig.oxygen);
  const satColor1 = valueColor(latestData?.sat1_pct, sensorConfig.saturation);
  const satColor2 = valueColor(latestData?.sat2_pct, sensorConfig.saturation);
  const tdoColor1 = valueColor(latestData?.temp_do1, sensorConfig.temperature);
  const tdoColor2 = valueColor(latestData?.temp_do2, sensorConfig.temperature);

  return (
    <>
      <div className="section-title">
        <span className="section-icon">📈</span>
        Línea de Tiempo
      </div>
      <div className="charts-grid fade-in stagger-3">
        {/* Temperature Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title">Temperaturas</div>
            <div className="chart-badge orange">
              {latestData.temp1?.toFixed(1)}°C / {latestData.temp2?.toFixed(1)}°C
            </div>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={history} margin={isMobile ? { left: -15, right: 5 } : undefined}>
              <defs>
                <linearGradient id="gradTemp1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={tempColor1} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={tempColor1} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gradTemp2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={tempColor2} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={tempColor2} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={fontSize} tickMargin={5} />
              <YAxis stroke="#64748b" fontSize={fontSize} domain={['auto', 'auto']} width={35} />
              <Tooltip content={<CustomTooltip unit="°C" />} />
              <Area type="monotone" dataKey="temp1" stroke={tempColor1} strokeWidth={2} fillOpacity={1} fill="url(#gradTemp1)" name="Temp 1" />
              <Area type="monotone" dataKey="temp2" stroke={tempColor2} strokeWidth={2} fillOpacity={1} fill="url(#gradTemp2)" name="Temp 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Oxygen Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title">Oxígeno Disuelto</div>
            <div className="chart-badge cyan">
              {latestData.do1_mgL?.toFixed(2)} / {latestData.do2_mgL?.toFixed(2)} mg/L
            </div>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={history} margin={isMobile ? { left: -15, right: 5 } : undefined}>
              <defs>
                <linearGradient id="gradDO1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={doColor1} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={doColor1} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gradDO2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={doColor2} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={doColor2} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={fontSize} tickMargin={5} />
              <YAxis stroke="#64748b" fontSize={fontSize} domain={['auto', 'auto']} width={35} />
              <Tooltip content={<CustomTooltip unit="mg/L" />} />
              <Area type="monotone" dataKey="do1_mgL" stroke={doColor1} strokeWidth={2} fillOpacity={1} fill="url(#gradDO1)" name="DO 1" />
              <Area type="monotone" dataKey="do2_mgL" stroke={doColor2} strokeWidth={2} fillOpacity={1} fill="url(#gradDO2)" name="DO 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Saturation Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title">Saturación</div>
            <div className="chart-badge purple">
              {latestData.sat1_pct?.toFixed(1)}% / {latestData.sat2_pct?.toFixed(1)}%
            </div>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={history} margin={isMobile ? { left: -15, right: 5 } : undefined}>
              <defs>
                <linearGradient id="gradSat1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={satColor1} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={satColor1} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gradSat2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={satColor2} stopOpacity={0.35}/>
                  <stop offset="95%" stopColor={satColor2} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={fontSize} tickMargin={5} />
              <YAxis stroke="#64748b" fontSize={fontSize} domain={['auto', 'auto']} width={35} />
              <Tooltip content={<CustomTooltip unit="%" />} />
              <Area type="monotone" dataKey="sat1_pct" stroke={satColor1} strokeWidth={2} fillOpacity={1} fill="url(#gradSat1)" name="Sat 1" />
              <Area type="monotone" dataKey="sat2_pct" stroke={satColor2} strokeWidth={2} fillOpacity={1} fill="url(#gradSat2)" name="Sat 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sensor Temperature Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title">Temperatura Sensores DO</div>
            <div className="chart-badge cyan">
              {latestData.temp_do1?.toFixed(1)}°C / {latestData.temp_do2?.toFixed(1)}°C
            </div>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={history} margin={isMobile ? { left: -15, right: 5 } : undefined}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={fontSize} tickMargin={5} />
              <YAxis stroke="#64748b" fontSize={fontSize} domain={['auto', 'auto']} width={35} />
              <Tooltip content={<CustomTooltip unit="°C" />} />
              <Line type="monotone" dataKey="temp_do1" stroke={tdoColor1} strokeWidth={2} dot={false} name="Temp DO 1" />
              <Line type="monotone" dataKey="temp_do2" stroke={tdoColor2} strokeWidth={2} dot={false} name="Temp DO 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

